
import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { contentTypeOptions } from "./ContentTypeOptions";
import { ContentItemForm as ContentItemFormType } from "@/hooks/useLessonContent";
import FileUploadForm from "./FileUploadForm";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ContentViewer from "@/components/course/ContentViewer";

interface ContentItemFormProps {
  currentContentItem: ContentItemFormType;
  setCurrentContentItem: (item: ContentItemFormType) => void;
  lessonId?: string;
}

const ContentItemForm: React.FC<ContentItemFormProps> = ({ 
  currentContentItem, 
  setCurrentContentItem,
  lessonId
}) => {
  // Handle file upload complete
  const handleFileUploaded = (filePath: string, fileName: string) => {
    setCurrentContentItem({ 
      ...currentContentItem, 
      file_path: filePath,
      content_type: filePath.endsWith('.pdf') ? 'pdf' : currentContentItem.content_type
    });
  };
  
  const hasContent = currentContentItem.file_path || currentContentItem.url;
  
  return (
    <Tabs defaultValue="details">
      <TabsList className="mb-4">
        <TabsTrigger value="details">Content Details</TabsTrigger>
        <TabsTrigger value="files">PDF Upload</TabsTrigger>
        {hasContent && <TabsTrigger value="preview">Content Preview</TabsTrigger>}
      </TabsList>
      
      <TabsContent value="details" className="space-y-4">
        {/* Content Type */}
        <div className="space-y-2">
          <Label htmlFor="content_type">Content Type</Label>
          <Select 
            value={currentContentItem.content_type} 
            onValueChange={(value) => setCurrentContentItem({ 
              ...currentContentItem, 
              content_type: value 
            })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select content type" />
            </SelectTrigger>
            <SelectContent>
              {contentTypeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <div className="flex items-center">
                    <option.icon className="mr-2 h-4 w-4" />
                    <span>{option.label}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Title */}
        <div className="space-y-2">
          <Label htmlFor="title">Title</Label>
          <Input
            id="title"
            placeholder="Enter content title"
            value={currentContentItem.title}
            onChange={(e) => 
              setCurrentContentItem({ ...currentContentItem, title: e.target.value })
            }
          />
        </div>
        
        {/* Description */}
        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            placeholder="Enter content description"
            value={currentContentItem.description}
            onChange={(e) => 
              setCurrentContentItem({ ...currentContentItem, description: e.target.value })
            }
          />
        </div>
        
        {/* URL (for videos, links, etc.) */}
        <div className="space-y-2">
          <Label htmlFor="url">URL</Label>
          <Input
            id="url"
            placeholder="Enter URL (for videos, links, etc.)"
            value={currentContentItem.url}
            onChange={(e) => 
              setCurrentContentItem({ ...currentContentItem, url: e.target.value })
            }
          />
          <p className="text-xs text-gray-500">For videos, external links, or web resources</p>
        </div>
        
        {/* Duration */}
        <div className="space-y-2">
          <Label htmlFor="duration">Duration in seconds (optional)</Label>
          <Input
            id="duration"
            type="number"
            placeholder="Enter duration in seconds"
            value={currentContentItem.duration}
            onChange={(e) => 
              setCurrentContentItem({ ...currentContentItem, duration: e.target.value })
            }
          />
        </div>
        
        {/* Required Switch */}
        <div className="flex items-center justify-between">
          <Label htmlFor="required">Mark as required content</Label>
          <Switch
            id="required"
            checked={currentContentItem.is_required}
            onCheckedChange={(checked) => 
              setCurrentContentItem({ ...currentContentItem, is_required: checked })
            }
          />
        </div>
      </TabsContent>
      
      <TabsContent value="files">
        {lessonId && (
          <FileUploadForm
            lessonId={lessonId}
            onFileUploaded={handleFileUploaded}
            currentFilePath={currentContentItem.file_path || null}
          />
        )}
      </TabsContent>

      {hasContent && (
        <TabsContent value="preview">
          <div className="border rounded-md p-4 bg-gray-50">
            <h3 className="text-lg font-medium mb-4">Content Preview</h3>
            
            <ContentViewer 
              contentType={currentContentItem.content_type}
              filePath={currentContentItem.file_path || null}
              url={currentContentItem.url || null}
              title={currentContentItem.title || "Content Preview"}
            />
          </div>
        </TabsContent>
      )}
    </Tabs>
  );
};

export default ContentItemForm;
